import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import { useAuth } from "../context/AuthContext"; 
import { useTheme } from "../context/ThemeContext"; 
import UserTopbar from "../components/UserTopbar"; 
import PaymentModal from "../components/PaymentModal"; 
import adminAxios from "../admin/adminAxios"; // Isolated tenant-aware client replacing standard api

// --- SYSTEMATIC TOAST COMPONENT ---
const Toast = ({ message, type, onClose, colors }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', bottom: '100px', left: '50%', transform: 'translateX(-50%)',
      backgroundColor: type === 'error' ? colors.danger : colors.success,
      color: '#fff', padding: '12px 24px', borderRadius: '12px', zIndex: 9999,
      fontWeight: 'bold', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', fontSize: '14px'
    }}>
      {message}
    </div>
  );
};

// --- REFINED TRANSACTION ROW WITH CURRENCY CONVERSION ---
const TransactionRow = ({ tx, colors }) => {
  const isDeposit = tx.type === 'deposit' || tx.type === 'credit';
  const status = (tx.status || 'pending').toLowerCase();
  
  let displayAmount = Number(tx.amount);
  if (tx.currency === "INR") {
    const exchangeRate = 83.15;
    displayAmount = displayAmount / exchangeRate;
  }

  const getStatusColor = () => {
    if (status === 'approved' || status === 'completed' || status === 'settled') return colors.success;
    if (status === 'rejected' || status === 'failed') return colors.danger;
    return '#f5f2ee'; 
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '16px', backgroundColor: colors.card, borderRadius: '12px',
      marginBottom: '10px', border: `1px solid ${colors.border}`
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '11px', color: colors.muted, fontWeight: '700', letterSpacing: '0.5px' }}>
          {new Date(tx.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} • {tx.type?.toUpperCase()}
        </div>
        <div style={{ fontSize: '14px', color: colors.text, fontWeight: '800', marginTop: '4px' }}>
          {tx.description || (isDeposit ? "Capital Inflow" : "Settlement Outflow")}
        </div>
        <div style={{ fontSize: '10px', color: colors.brand, marginTop: '6px', fontFamily: 'monospace', fontWeight: 'bold' }}>
          TRXID: {tx.txid || (tx._id ? tx._id.toUpperCase() : "PROCESSING...")}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: '100px' }}>
        <div style={{ fontSize: '16px', fontWeight: '900', color: isDeposit ? colors.success : colors.danger }}>
          {isDeposit ? '+' : '-'}${displayAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div style={{ 
          fontSize: '10px', fontWeight: '900', color: getStatusColor(),
          textTransform: 'uppercase', marginTop: '4px'
        }}>
          ● {status}
        </div>
      </div>
    </div>
  );
};

const OrderCard = memo(({ order, isExpanded, onToggle, onPayment, API_BASE, isMobile, colors }) => {
  if (!order || !order._id) return null;

  const status = String(order.status || "pending").toLowerCase();
  const payStatus = String(order.paymentStatus || "unpaid").toLowerCase();
  
  const baseValue = Number(
    order.baseValue || 
    order.price || 
    (order.productId && typeof order.productId === 'object' ? (order.productId.baseValue || order.productId.price) : 0) || 
    0
  );

  const premium = Number(
    order.profit || 
    (order.productId && typeof order.productId === 'object' ? (order.productId.profit || order.productId.commissionYield) : 0) || 
    0
  ); 

  const totalValuation = baseValue + premium;

  const getImgUrl = () => {
    const rawImg = order.image || (order.productId && order.productId.image);
    if (!rawImg) return "https://via.placeholder.com/150?text=MARITIME+ASSET";
    if (rawImg.startsWith("http")) return rawImg;
    const cleanPath = rawImg.replace(/^\/+/, "").replace(/^uploads\//, "");
    return `${API_BASE}/uploads/${cleanPath}`;
  };

  const getStatusConfig = (s) => {
    switch(s) {
      case "completed": return { label: "ARCHIVED", color: colors.success };
      case "processing": return { label: "AUDIT IN PROGRESS", color: "#52d358" };
      case "shipped": return { label: "IN TRANSIT", color: "#3B82F6" };
      case "delivered": return { label: "PORT ARRIVAL", color: colors.brand };
      case "assigned": return { label: "ASSIGNED", color: "#64748B" };
      case "pending": return { label: "PENDING SETTLEMENT", color: colors.danger };
      default: return { label: "IN REVIEW", color: colors.muted };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div style={{
      backgroundColor: colors.card, borderRadius: "16px", marginBottom: "16px",
      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.2)", border: `1px solid ${colors.border}`,
      overflow: "hidden", transition: "all 0.3s ease",
      transform: isExpanded ? "scale(1.01)" : "scale(1)"
    }}>
      <div onClick={onToggle} style={{ padding: isMobile ? "15px" : "20px", display: "flex", cursor: "pointer", gap: "20px", alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <img
            src={getImgUrl()}
            alt="Manifest"
            style={{ width: "75px", height: "75px", borderRadius: "12px", objectFit: "cover", filter: (status === 'completed' || status === 'delivered') ? 'grayscale(0.8)' : 'none' }}
          />
          {(status === 'completed' || status === 'delivered') && <div style={styles.badgeSeal}>SETTLED</div>}
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "10px", color: colors.brand, fontWeight: "900", letterSpacing: "1.5px" }}>MANIFEST {order._id.slice(-8).toUpperCase()}</div>
          <h3 style={{ margin: "4px 0 8px 0", fontSize: "16px", fontWeight: "800", color: colors.text }}>
            {order.name || (order.productId && order.productId.name) || "Logistics Asset"}
          </h3>
          <div style={{ fontSize: "18px", fontWeight: "900", color: colors.text }}>
            ${baseValue.toLocaleString()} <span style={{fontSize: '10px', color: colors.muted}}>USD</span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{ 
            backgroundColor: config.color, color: "#fff", padding: "6px 10px", 
            borderRadius: "6px", fontSize: "10px", fontWeight: "900", textTransform: "uppercase" 
            }}>
            {config.label}
          </div>
          <div style={{ fontSize: "10px", color: colors.muted, marginTop: "10px", fontWeight: "700", letterSpacing: '1px' }}>
            {status === "completed" || status === "delivered" || payStatus === "paid" ? "🟢 SETTLED" : "🔴 UNPAID"}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: "25px", borderTop: `1px solid ${colors.border}`, backgroundColor: "rgba(0,0,0,0.03)" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr", gap: "25px", marginBottom: "25px" }}>
            <div>
              <div style={{...styles.detailLabel, color: colors.brand}}>ASSET SPECIFICATIONS</div>
              <p style={{...styles.detailText, color: colors.muted}}>
                {order.details || (order.productId && order.productId.details) || "Verified maritime supply asset. Logistics pathing authenticated by central command."}
              </p>
            </div>
            
            <div style={{ backgroundColor: colors.bg, padding: "20px", borderRadius: "12px", border: `1px solid ${colors.brand}` }}>
              <div style={{...styles.detailLabel, color: colors.brand}}>FINANCIAL SETTLEMENT</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
                <span style={{ fontSize: "13px", color: colors.muted }}>Base Value:</span>
                <span style={{ fontSize: "13px", fontWeight: "700", color: colors.text }}>${baseValue.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px" }}>
                <span style={{ fontSize: "13px", color: colors.muted }}>Yield:</span>
                <span style={{ fontSize: "13px", fontWeight: "700", color: colors.success }}>+${premium.toLocaleString()}</span>
              </div>
              <div style={{ height: "1px", background: colors.border, margin: "12px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "14px", fontWeight: "900", color: colors.brand }}>Gross Yield:</span>
                <span style={{ fontSize: "16px", fontWeight: "900", color: colors.text }}>${totalValuation.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {status !== "completed" && status !== "delivered" && payStatus !== "paid" ? (
            <button
              onClick={(e) => { e.stopPropagation(); onPayment(baseValue, order._id); }}
              style={{...styles.btnAuthorize, background: colors.brand}}
            >
              MAKE PAYMENT
            </button>
          ) : (
            <div style={{...styles.auditStamp, border: `1px dashed ${colors.border}`, color: colors.muted}}>
              DIGITAL SIGNATURE VERIFIED - SYSTEM ARCHIVED
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function Orders() {
  const { userId, socket } = useAuth(); 
  const { colors } = useTheme(); 
  const isMounted = useRef(true);
  
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]); 
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [toast, setToast] = useState(null);

  const [activeTab, setActiveTab] = useState("active"); 
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  
  // Retrieve isolated tenant context identifier matching other modules
  const tenantId = localStorage.getItem("adminId") || localStorage.getItem("tenantId") || "global";

  const API_URL_RAW = process.env.REACT_APP_API_URL || "https://maritime-supply-suite-backend.onrender.com";
  const API_BASE = API_URL_RAW.replace(/\/api\/?$/, "");

  const loadAllData = useCallback(async () => {
    if (!userId) return;
    try {
      const [orderRes, txRes] = await Promise.all([
        adminAxios.get(`/orders/user/${userId}`, { params: { tenantId } }),
        adminAxios.get(`/transactions/user/${userId}`, { params: { tenantId } })
      ]);
      if (isMounted.current) {
        setOrders(Array.isArray(orderRes.data) ? orderRes.data : []);
        setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
        setLoading(false);
      }
    } catch (err) { 
        console.error("[System] Critical Tenant Sync Error:", err); 
        if (isMounted.current) setLoading(false);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    isMounted.current = true;
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    
    if (userId) loadAllData();

    if (socket && userId) {
      socket.on("registryRefresh", loadAllData);
      socket.on("orderUpdate", loadAllData);
      socket.on("payment_status_updated", (data) => {
        loadAllData();
        setToast({ 
          msg: `UPLINK SUCCESS: Transaction ${data.status.toUpperCase()}`, 
          type: data.status === 'approved' ? 'success' : 'error' 
        });
      });
    }
    
    return () => { 
      isMounted.current = false; 
      window.removeEventListener("resize", handleResize);
      if (socket) {
        socket.off("registryRefresh");
        socket.off("orderUpdate");
        socket.off("payment_status_updated");
      }
    };
  }, [loadAllData, userId, socket]);

  const handlePaymentClick = useCallback((amount, orderId) => {
    setSelectedAmount(amount);
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  }, []);

  const notify = (msg, type) => setToast({ msg, type });

  const filteredOrders = orders.filter(o => {
    if (!o || !o.status) return false;
    const s = o.status.toLowerCase();
    if (s === "assigned") return false;
    const isHistory = (s === "completed" || s === "delivered");
    return activeTab === "completed" ? isHistory : !isHistory;
  });

  const historyCount = orders.filter(o => ["completed", "delivered"].includes(o.status.toLowerCase())).length;
  const activeCount = orders.filter(o => {
    const s = o.status.toLowerCase();
    return s !== "assigned" && s !== "completed" && s !== "delivered";
  }).length;

  return (
    <div style={{...styles.layout, backgroundColor: colors.bg}} data-tenant-scope={tenantId}>
      
      {toast && (
        <Toast 
          message={toast.msg} 
          type={toast.type} 
          onClose={() => setToast(null)} 
          colors={colors} 
        />
      )}

      <PaymentModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        colors={colors} 
        initialAmount={selectedAmount}
        orderId={selectedOrderId}
        manifestId={selectedOrderId ? String(selectedOrderId).slice(-8).toUpperCase() : 'PENDING'}
        onNotify={notify}
        refreshData={loadAllData}
      />
      <UserTopbar title="" />

      <div style={styles.tabContainer}>
        <div style={styles.tabBar}>
          <div 
            onClick={() => setActiveTab("active")}
            style={{ 
              ...styles.tab, 
              borderBottom: activeTab === "active" ? `3px solid ${colors.brand}` : "3px solid transparent", 
              color: activeTab === "active" ? colors.text : colors.muted 
            }}
          >
            ACTIVE MANIFESTS ({activeCount})
          </div>
          <div 
            onClick={() => setActiveTab("completed")}
            style={{ 
              ...styles.tab, 
              borderBottom: activeTab === "completed" ? `3px solid ${colors.brand}` : "3px solid transparent", 
              color: activeTab === "completed" ? colors.text : colors.muted 
            }}
          >
            HISTORY ({historyCount})
          </div>
          <div 
            onClick={() => setActiveTab("payments")}
            style={{ 
              ...styles.tab, 
              borderBottom: activeTab === "payments" ? `3px solid ${colors.brand}` : "3px solid transparent", 
              color: activeTab === "payments" ? colors.text : colors.muted 
            }}
          >
            PAYMENT HISTORY
          </div>
        </div>
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={{...styles.statusMsg, color: colors.brand}}>
             <span className="pulse">●</span> Accessing Secure Manifest Records...
          </div>
        ) : activeTab === "payments" ? (
          transactions.length === 0 ? (
            <div style={{...styles.empty, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
              <div style={{fontSize: '40px', marginBottom: '15px'}}>💳</div>
              <p style={{ color: colors.muted, fontSize: "14px", fontWeight: "700", letterSpacing: '1px' }}>
                NO TRANSACTION LOGS FOUND
              </p>
            </div>
          ) : (
            transactions.map((tx) => <TransactionRow key={tx._id} tx={tx} colors={colors} />)
          )
        ) : (
          filteredOrders.length === 0 ? (
            <div style={{...styles.empty, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
              <div style={{fontSize: '40px', marginBottom: '15px'}}>⚓</div>
              <p style={{ color: colors.muted, fontSize: "14px", fontWeight: "700", letterSpacing: '1px' }}>
                {activeTab === "active" ? "NO ACTIVE MANIFEST IN TRANSIT" : "HISTORY IS CURRENTLY EMPTY"}
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <OrderCard 
                key={order._id}
                order={order}
                isExpanded={expandedId === order._id}
                onToggle={() => setExpandedId(expandedId === order._id ? null : order._id)}
                onPayment={handlePaymentClick}
                API_BASE={API_BASE}
                isMobile={isMobile}
                colors={colors}
              />
            ))
          )
        )}
      </div>

      <style>{`
        .pulse { color: ${colors.brand}; margin-right: 10px; animation: blink 1.5s infinite; }
        @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
      `}</style>
    </div>
  );
}

const styles = {
  layout: { minHeight: "100vh", paddingTop: "155px", paddingBottom: "80px", fontFamily: "'Inter', sans-serif", transition: 'background-color 0.3s ease' },
  tabContainer: {
    position: 'fixed', top: '150px', left: 0, right: 0, zIndex: 1001, 
    display: 'flex', justifyContent: 'center', backgroundColor: 'inherit'
  },
  tabBar: { display: "flex", justifyContent: "center", gap: "25px", borderBottom: '1px solid rgba(255,255,255,0.05)', width: '100%', maxWidth: '800px' },
  tab: { padding: "12px 10px", cursor: "pointer", fontWeight: "900", fontSize: "10px", letterSpacing: "1px", transition: "0.3s", whiteSpace: 'nowrap' },
  content: { maxWidth: "800px", margin: "45px auto 0 auto", padding: "0 20px" },
  statusMsg: { textAlign: "center", padding: "120px 0", fontSize: "14px", fontWeight: "800", letterSpacing: '1px' },
  empty: { textAlign: "center", padding: "100px 40px", borderRadius: "24px" },
  detailLabel: { fontSize: "10px", fontWeight: "900", letterSpacing: "1.5px", marginBottom: "12px" },
  detailText: { fontSize: "14px", margin: 0, lineHeight: "1.7", fontWeight: "500" },
  btnAuthorize: { 
    width: "100%", padding: "18px", color: "#fff", 
    border: "none", borderRadius: "12px", fontWeight: "900", cursor: "pointer", 
    fontSize: "13px", letterSpacing: "2px", boxShadow: "0 4px 15px rgba(109, 155, 195, 0.3)" 
  },
  badgeSeal: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-15deg)', background: 'rgba(16, 185, 129, 0.95)', color: '#fff', padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '900', border: '1px solid #fff', zIndex: 5 },
  auditStamp: { textAlign: 'center', padding: '20px', borderRadius: '12px', fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px', background: 'rgba(255,255,255,0.03)' }
};

export default Orders;